import React, { useEffect } from 'react';

// Full-size view of a post's content. Carousels page through with ← / →.
// Edit buttons appear only while the session is still editable.
const isVideo = (url) => /\.(mp4|mov|webm)(\?|$)/i.test(url || '');

const btn = (bg, color, border) => ({
  fontSize: '13px', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
  background: bg, color, border: `1px solid ${border}`, fontFamily: 'inherit', whiteSpace: 'nowrap'
});
const arrow = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '48px', height: '48px',
  borderRadius: '50%', border: '1px solid #333', background: 'rgba(20,20,20,0.85)', color: '#fff',
  fontSize: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
};

export default function MediaLightbox({ post, subName, index, locked, busy, onIndex, onClose, onRemove, onRepick }) {
  const isCarousel = Array.isArray(post.carousel_items) && post.carousel_items.length > 1;
  const images = isCarousel
    ? post.carousel_items
    : [{ content_url: post.content_url, thumbnail_url: post.thumbnail_url }];
  const i = Math.min(Math.max(index || 0, 0), images.length - 1);
  const cur = images[i] || {};
  const src = cur.content_url || cur.thumbnail_url;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && i < images.length - 1) onIndex(i + 1);
      else if (e.key === 'ArrowLeft' && i > 0) onIndex(i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [i, images.length, onClose, onIndex]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.94)', zIndex: 1200, display: 'flex', flexDirection: 'column' }}>
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px', borderBottom: '1px solid #222', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', color: '#ff4500', fontWeight: 600 }}>
            {subName}{isCarousel ? ` · 📸 image ${i + 1} of ${images.length}` : ''}
          </div>
          <div style={{ fontSize: '13px', color: post.title ? '#fff' : '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.title || 'No title set'}</div>
          {post.pick_reason && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>🎯 picked: {post.pick_reason}</div>}
        </div>
        {!locked && (
          <>
            {post.subreddit_id && (
              isCarousel ? (
                <>
                  <button disabled={busy} onClick={() => onRepick(i)} title="Replace just this image with another matching one from the same folder" style={btn('#1a1a1a', '#ddd', '#444')}>
                    {busy ? '…' : '🎲 Swap this image'}
                  </button>
                  <button disabled={busy} onClick={() => onRepick(null)} title="Build a different carousel from another matching folder" style={btn('#1a1a1a', '#ddd', '#444')}>
                    {busy ? '…' : '🎲 Re-roll carousel'}
                  </button>
                </>
              ) : (
                <button disabled={busy} onClick={() => onRepick(null)} title="Swap in a different matched item" style={btn('#1a1a1a', '#ddd', '#444')}>
                  {busy ? '…' : '🎲 Repick'}
                </button>
              )
            )}
            <button disabled={busy} onClick={() => onRemove(isCarousel ? i : null)} style={btn('#2a0f0f', '#f87171', '#7f1d1d')}>
              {isCarousel ? '🗑 Remove this image' : '🗑 Remove'}
            </button>
          </>
        )}
        <button onClick={onClose} title="Close (Esc)" style={btn('transparent', '#aaa', '#444')}>✕ Close</button>
      </div>

      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 72px', minHeight: 0 }}>
        {src ? (
          isVideo(src)
            ? <video key={src} src={src} controls autoPlay loop muted onClick={e => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px' }} />
            : <img key={src} src={src} alt="" onClick={e => e.stopPropagation()}
                onError={(e) => { if (cur.thumbnail_url && e.target.src !== cur.thumbnail_url) e.target.src = cur.thumbnail_url; }}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px' }} />
        ) : <div style={{ color: '#555' }}>No content</div>}
        {i > 0 && <button onClick={e => { e.stopPropagation(); onIndex(i - 1); }} style={{ ...arrow, left: '16px' }}>‹</button>}
        {i < images.length - 1 && <button onClick={e => { e.stopPropagation(); onIndex(i + 1); }} style={{ ...arrow, right: '16px' }}>›</button>}
      </div>

      {isCarousel && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '6px', justifyContent: 'center', padding: '10px 16px 16px', overflowX: 'auto' }}>
          {images.map((ci, k) => (
            <img key={ci.vault_item_id || k} src={ci.thumbnail_url || ci.content_url} alt="" onClick={() => onIndex(k)}
              style={{ height: '56px', width: '56px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer', flexShrink: 0,
                border: k === i ? '2px solid #ff4500' : '1px solid #333', opacity: k === i ? 1 : 0.6 }} />
          ))}
        </div>
      )}
    </div>
  );
}
